/*
 * Conservative Performance Optimizations for CLS and INP
 * Minimal changes to avoid breaking existing functionality
 * Includes ResizeObserver loop prevention
 */

// ===== CONSERVATIVE CLS & INP MONITORING =====

// Basic performance monitoring without aggressive interventions
(function initPerformanceMonitoring() {
  // Only track performance, don't modify behavior
  function trackPerformance() {
    let clsValue = 0;
    let interactions = [];
    
    // Track CLS without interfering
    if ('PerformanceObserver' in window) {
      try {
        const clsObserver = new PerformanceObserver((list) => {
          list.getEntries().forEach((entry) => {
            if (!entry.hadRecentInput) {
              clsValue += entry.value;
              // Only log significant shifts, don't modify DOM
              if (entry.value > 0.05) {
                console.log('CLS detected:', entry.value.toFixed(4), 'Total:', clsValue.toFixed(4));
              }
            }
          });
        });
        
        clsObserver.observe({ entryTypes: ['layout-shift'] });
      } catch (e) {
        // Silently fail if not supported
      }
    }
    
    // Track slow interactions without interfering
    ['click', 'touchstart'].forEach(eventType => {
      document.addEventListener(eventType, function(event) {
        const startTime = performance.now();
        
        // Use a single RAF to measure response time
        requestAnimationFrame(() => {
          const responseTime = performance.now() - startTime;
          if (responseTime > 100) {
            interactions.push({
              type: eventType,
              time: responseTime,
              target: event.target.tagName
            });
            
            // Keep only recent interactions
            if (interactions.length > 5) {
              interactions.shift();
            }
          }
        });
      }, { passive: true });
    });
  }
  
  // Initialize monitoring without affecting theme functionality
  setTimeout(trackPerformance, 2000); // Wait for theme to fully load
})();

// ===== MINIMAL IMAGE OPTIMIZATION =====

// Only add aspect ratios where they don't exist and won't break layout
(function setupMinimalImageOptimization() {
  function addConservativeAspectRatios() {
    // Only target product images and avoid breaking existing layouts
    const images = document.querySelectorAll('.product-card img[width][height]:not([data-aspect-ratio-processed]), .card img[width][height]:not([data-aspect-ratio-processed])');
    
    images.forEach(img => {
      // Skip if image is already in a container that might handle aspect ratio
      if (img.closest('.media, .image-wrapper, .aspect-ratio, [style*="aspect-ratio"]')) {
        return;
      }
      
      const width = parseInt(img.getAttribute('width'));
      const height = parseInt(img.getAttribute('height'));
      
      if (width && height && width > 0 && height > 0) {
        // Only add aspect ratio as CSS, don't create wrappers
        const aspectRatio = (width / height).toFixed(3);
        img.style.aspectRatio = aspectRatio;
        img.setAttribute('data-aspect-ratio-processed', 'true');
      }
    });
  }
  
  // Run once after page load, don't use aggressive mutation observers
  setTimeout(addConservativeAspectRatios, 1000);
})();

// ===== CONSERVATIVE CAROUSEL FIX =====

// Only set min-height for carousels that don't have it
(function fixCarouselCLS() {
  setTimeout(() => {
    const carousels = document.querySelectorAll('.flickity-enabled:not([style*="min-height"]), [data-flickity]:not([style*="min-height"])');
    
    carousels.forEach(carousel => {
      // Only add min-height if carousel has no explicit height set
      if (!carousel.style.height && !carousel.style.minHeight) {
        carousel.style.minHeight = '250px'; // Conservative default
      }
    });
  }, 500); // Run after Flickity initialization
})();

// ===== BASIC BUTTON FEEDBACK =====

// Simple click feedback without breaking existing functionality
(function addBasicFeedback() {
  // Use event delegation but only for visual feedback
  document.addEventListener('click', (event) => {
    const button = event.target.closest('button:not([data-no-feedback]), .btn:not([data-no-feedback]), [role="button"]:not([data-no-feedback])');
    
    if (button && !button.disabled) {
      // Simple scale effect that won't interfere with existing animations
      const originalTransform = button.style.transform;
      button.style.transform = (originalTransform || '') + ' scale(0.95)';
      
      setTimeout(() => {
        button.style.transform = originalTransform;
      }, 150);
    }
  }, { passive: false });
})();

// ===== RESIZEOBSERVER MONITORING =====

// Monitor for ResizeObserver issues without duplicating functionality
(function setupResizeObserverMonitoring() {
  // Only add monitoring if the safe function isn't already available
  if (typeof window.createSafeResizeObserver !== 'function') {
    console.warn('ResizeObserver fix script may not have loaded properly');
  }
  
  // Simple monitoring without overriding console.error to avoid conflicts
  let loopDetectionCount = 0;
  
  // Listen for ResizeObserver errors through window error event
  window.addEventListener('error', function(event) {
    if (event.message && event.message.includes('ResizeObserver loop')) {
      loopDetectionCount++;
      if (loopDetectionCount <= 3) { // Only log first few occurrences
        console.debug('ResizeObserver loop detected (event #' + loopDetectionCount + ')');
      }
      // Don't prevent default - let the fix script handle it
    }
  }, { passive: true });
})();

// ===== INITIALIZATION =====

// Simple initialization without complex scheduling
(function initOptimizations() {
  console.log('🚀 Conservative performance optimizations loaded with ResizeObserver protection');
  
  // Emit event for compatibility
  document.dispatchEvent(new CustomEvent('performance:optimized', {
    detail: { 
      timestamp: performance.now(),
      mode: 'conservative',
      resizeObserverProtection: true
    }
  }));
})();
